import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { api, toTokens } from './api';
import { setTenantLocale } from './format';
import { session } from './session';
import { applyBrandColor } from './theme';

export interface Me {
  user: { id: string; phone: string | null; email: string | null; isPlatformAdmin: boolean };
  tenant: { id: string; slug: string; name: string; timezone: string; currency: string; languages: string[] };
  /** Null for a platform administrator viewing a stable they are not a member of. */
  person: { id: string; firstName: string; lastName: string; referralCode: string } | null;
  roles: string[];
  permissions: Array<{ key: string; scope: 'own' | 'assigned' | 'tenant' }>;
}

export interface Stable {
  id: string;
  slug: string;
  name: string;
  personId: string;
}

export interface Branding {
  name: string;
  logo: { url: string; thumbUrl: string | null } | null;
  brandColors: { primary: string; secondary: string; accent: string };
  contact: Record<string, string>;
}

interface AuthResult {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: Me['user'];
}

interface AuthValue {
  signedIn: boolean;
  tenant: string | null;
  me: Me | undefined;
  meLoading: boolean;
  meError: unknown;
  retryMe(): void;
  isPlatformAdmin: boolean;
  branding: Branding | undefined;
  features: Set<string>;
  can(permission: string | string[]): boolean;
  scopeOf(permission: string): 'own' | 'assigned' | 'tenant' | null;
  loginWithPassword(email: string, password: string): Promise<AuthResult>;
  requestOtp(phone: string): Promise<{ devCode?: string }>;
  verifyOtp(phone: string, code: string): Promise<AuthResult>;
  stables(): Promise<Stable[]>;
  selectTenant(slug: string | null): void;
  logout(): Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(session.subscribe, session.get);
  const qc = useQueryClient();
  const signedIn = Boolean(state.tokens);

  const meQuery = useQuery({
    queryKey: ['me', state.tenant],
    queryFn: () => api.get<Me>('me'),
    enabled: signedIn && Boolean(state.tenant),
    staleTime: 60_000,
    retry: false,
  });
  const brandingQuery = useQuery({
    queryKey: ['branding', state.tenant],
    queryFn: () => api.get<Branding>('public/tenant'),
    enabled: Boolean(state.tenant),
    staleTime: 5 * 60_000,
  });
  const featuresQuery = useQuery({
    queryKey: ['features', state.tenant],
    queryFn: () => api.get<Array<{ key: string; enabled: boolean }>>('admin/features'),
    enabled: signedIn && Boolean(state.tenant) && Boolean(meQuery.data),
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (meQuery.data) setTenantLocale(meQuery.data.tenant.timezone, meQuery.data.tenant.currency);
  }, [meQuery.data]);
  useEffect(() => {
    applyBrandColor(brandingQuery.data?.brandColors?.primary);
  }, [brandingQuery.data]);

  const permissions = useMemo(() => new Map((meQuery.data?.permissions ?? []).map((p) => [p.key, p.scope])), [meQuery.data]);
  const isPlatformAdmin = Boolean(meQuery.data?.user.isPlatformAdmin ?? state.platformAdmin);

  const can = useCallback(
    (perm: string | string[]) => {
      if (isPlatformAdmin) return true;
      const list = (Array.isArray(perm) ? perm : [perm]).filter(Boolean);
      return list.some((p) => (p.endsWith('*') ? [...permissions.keys()].some((k) => k.startsWith(p.slice(0, -1))) : permissions.has(p)));
    },
    [permissions, isPlatformAdmin],
  );

  const afterAuth = (body: AuthResult) => {
    session.set({ tokens: toTokens(body), platformAdmin: Boolean(body.user?.isPlatformAdmin) });
    qc.clear();
    return body;
  };

  const value: AuthValue = {
    signedIn,
    tenant: state.tenant,
    me: meQuery.data,
    meLoading: meQuery.isLoading,
    meError: meQuery.error,
    retryMe: () => void meQuery.refetch(),
    isPlatformAdmin,
    branding: brandingQuery.data,
    features: new Set((featuresQuery.data ?? []).filter((f) => f.enabled).map((f) => f.key)),
    can,
    scopeOf: (p) => (isPlatformAdmin ? 'tenant' : permissions.get(p) ?? null),
    loginWithPassword: async (email, password) => afterAuth(await api.post<AuthResult>('auth/login', { email, password, deviceName: 'Admin panel' }, { tenant: null })),
    requestOtp: (phone) => api.post('auth/otp/request', { phone }, { tenant: null }),
    verifyOtp: async (phone, code) => afterAuth(await api.post<AuthResult>('auth/otp/verify', { phone, code, deviceName: 'Admin panel' }, { tenant: null })),
    stables: () => api.get<Stable[]>('auth/my-stables', { tenant: null }),
    selectTenant: (slug) => {
      session.set({ tenant: slug });
      qc.removeQueries();
    },
    logout: async () => {
      try {
        await api.post('auth/logout', {}, { tenant: null });
      } catch {
        // the session may already be gone
      }
      session.clear();
      qc.clear();
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside provider');
  return v;
}
